// apps/prompts/src/components/CategorySelect.tsx
// Select stylisé (dot couleur + icône Tabler) pour le filtre Catégorie.
// Composant maison — mirrors le pattern dropdown de @neuroforge/ui (AppSwitcher).
import {
  cn,
  AppIcon,
  IconCheck,
  IconChevronDown,
  useClickOutside,
  type AppIconProps,
} from '@neuroforge/ui'
import { useEffect, useId, useRef, useState } from 'react'

export interface CategoryOption {
  /** Valeur enum API ('all' inclus) */
  value: string
  label: string
  /** Couleur d'accent (dot) — optionnelle pour « Toutes » */
  color?: string
  icon?: AppIconProps['icon']
}

export interface CategorySelectProps {
  options: CategoryOption[]
  value: string
  onChange: (value: string) => void
  className?: string
}

/**
 * Select custom accessible (listbox) reprenant le rendu de l'ancienne liste :
 * pastille de couleur + icône Tabler + label. Fermeture au clic extérieur /
 * Échap, navigation clavier ↑/↓/Entrée.
 */
export default function CategorySelect({
  options,
  value,
  onChange,
  className,
}: CategorySelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()
  useClickOutside(ref, () => setOpen(false))

  const selected = options.find((o) => o.value === value) ?? options[0]
  const selectedIndex = Math.max(
    options.findIndex((o) => o.value === value),
    0,
  )
  const [activeIndex, setActiveIndex] = useState(selectedIndex)

  // Recale l'option active sur l'ouverture
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex)
  }, [open, selectedIndex])

  // Scroll l'option active dans la vue
  useEffect(() => {
    if (!open) return
    const node = listRef.current?.children[activeIndex] as HTMLElement | undefined
    node?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const choose = (val: string) => {
    onChange(val)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % options.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + options.length) % options.length)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      choose(options[activeIndex].value)
    }
  }

  return (
    <div ref={ref} className={cn('relative', className)} onKeyDown={onKeyDown}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold transition-all duration-150',
          'border-(--border) border bg-[var(--bg-soft)]',
          open
            ? 'text-(--accent) border-[var(--accent)]'
            : 'text-(--text-secondary) hover:border-[var(--accent)]',
        )}
      >
        {selected.color && (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: selected.color }}
          />
        )}
        {selected.icon && <AppIcon icon={selected.icon} size={16} aria-hidden />}
        <span className="truncate">{selected.label}</span>
        <AppIcon
          icon={IconChevronDown}
          size={16}
          className={cn(
            'text-(--muted) ml-auto shrink-0 transition-transform duration-200',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label="Catégorie"
          tabIndex={-1}
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-2xl p-1.5',
            'border-(--border) border bg-white shadow-xl dark:bg-slate-900',
          )}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === activeIndex
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => choose(opt.value)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors',
                    isSelected
                      ? 'text-(--accent) bg-(--accent-subtle)'
                      : isActive
                        ? 'text-(--text) bg-(--bg-elevated)'
                        : 'text-(--text-secondary)',
                  )}
                >
                  {opt.color ? (
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: opt.color }}
                    />
                  ) : (
                    <span className="w-2.5 shrink-0" />
                  )}
                  {opt.icon && <AppIcon icon={opt.icon} size={16} aria-hidden />}
                  <span className="truncate">{opt.label}</span>
                  {isSelected && (
                    <AppIcon
                      icon={IconCheck}
                      size={16}
                      className="text-(--accent) ml-auto shrink-0"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
